import {
  isValidCustomDisciplineBehavior,
  type CustomDisciplineBehavior,
} from "./behavior.js";
import type { CustomDisciplineGroupTypeInput } from "./types.js";
import {
  customWebCellValue,
  customWebOrder,
  type CustomWebColumn,
} from "./web-view.js";
import {
  releaseCustomDisciplineBehaviorRuntime,
  type CustomDisciplineEvaluateInput,
  type CustomDisciplineAthleteRecord,
} from "../custom-discipline-behavior/index.js";

export const CUSTOM_EXPORTS_KEY = "export_profiles";
export const EXPORT_TEMPLATE_MAX_BYTES = 5 * 1024 * 1024;
export type CustomExportFormat = "CSV" | "XLSX" | "PDF";
export type ExportCellStyle = {
  fontFamily?: string;
  fontSize?: number;
  bold?: boolean;
  italic?: boolean;
  underline?: boolean;
  color?: string;
  fill?: string;
  horizontal?: "left" | "center" | "right";
  vertical?: "top" | "middle" | "bottom";
  wrap?: boolean;
  border?: {
    style: "thin" | "medium" | "thick" | "dashed" | "dotted" | "double";
    color: string;
    sides: Array<"top" | "right" | "bottom" | "left">;
  };
};
export type CustomExportColumn = {
  key: string;
  label: string;
  fieldKey: string;
  enabled: boolean;
  valueType: "TEXT" | "NUMBER" | "BOOLEAN" | "DATE" | "AUTO";
  behavior: CustomDisciplineBehavior | null;
  excelColumn: string;
  width?: number;
  numberFormat?: string;
  style?: ExportCellStyle;
  headerStyle?: ExportCellStyle;
};
export type CustomExportBinding = {
  key: string;
  cell: string;
  sheet?: string;
  label: string;
  fieldKey: string;
  valueType: CustomExportColumn["valueType"];
  behavior: CustomDisciplineBehavior | null;
  style?: ExportCellStyle;
  numberFormat?: string;
};
export type CustomExportProfile = {
  key: string;
  name: string;
  enabled: boolean;
  formats: CustomExportFormat[];
  fileName: string;
  sheetName: string;
  columns: CustomExportColumn[];
  bindings: CustomExportBinding[];
  filter: CustomDisciplineBehavior | null;
  order: CustomDisciplineBehavior | null;
  section: CustomDisciplineBehavior | null;
  sort: Array<{ columnKey: string; direction: "asc" | "desc" }>;
  startRow: number;
  headerRow: number | null;
  rowStep: number;
  blankRowsBetweenSections: number;
  endRow: number | null;
  rowHeight: number;
  headerHeight: number;
  copyStyleFromRow: number | null;
  /** Insert extra athlete rows here, moving the rest of the template down. */
  templateDataEndRow?: number | null;
  clearMappedRange: boolean;
  defaultStyle: ExportCellStyle;
  headerStyle: ExportCellStyle;
  csv: {
    delimiter: "," | ";" | "\t";
    bom: boolean;
    includeHeader: boolean;
    protectTextFormulas: boolean;
  };
  pdf: {
    landscape: boolean;
    paperSize: "A4" | "A3";
    margin: number;
    title: string;
  };
  template: { name: string; base64: string; sheets: string[] } | null;
};
export type CustomExportProfiles = {
  version: 1;
  profiles: CustomExportProfile[];
};
export type ExportCellValue = string | number | boolean | Date | null;
export type PreparedExportRow = {
  athleteId: string;
  groupId: string;
  rowNumber: number;
  section: string;
  cells: Record<string, { value: ExportCellValue; style: ExportCellStyle }>;
};
export type PreparedCustomExport = {
  columns: CustomExportColumn[];
  rows: PreparedExportRow[];
  bindings: Array<CustomExportBinding & { value: ExportCellValue }>;
};

export function excelColumnNumber(column: string): number {
  if (!/^[A-Z]{1,3}$/.test(column)) return 0;
  const n = [...column].reduce((n, c) => n * 26 + c.charCodeAt(0) - 64, 0);
  return n <= 16384 ? n : 0;
}
export function excelColumnName(index: number): string {
  let result = "";
  for (let n = index; n > 0; n = Math.floor((n - 1) / 26))
    result = String.fromCharCode(65 + ((n - 1) % 26)) + result;
  return result;
}
export function customExportTemplateRowExpansion(
  profile: CustomExportProfile,
  rows: PreparedExportRow[],
): number {
  if (!profile.template || profile.templateDataEndRow == null) return 0;
  return rows.reduce(
    (extra, row) =>
      Math.max(extra, row.rowNumber - profile.templateDataEndRow!),
    0,
  );
}
const integer = (v: unknown, min: number, max: number) =>
  Number.isInteger(v) && Number(v) >= min && Number(v) <= max;
const object = (v: unknown): v is Record<string, unknown> =>
  !!v && typeof v === "object" && !Array.isArray(v);
const color = (v: unknown) =>
  typeof v === "string" && /^#[0-9a-f]{6}$/i.test(v);
function validStyle(s: unknown): boolean {
  if (s === undefined) return true;
  if (!object(s)) return false;
  const allowed = new Set([
    "fontFamily",
    "fontSize",
    "bold",
    "italic",
    "underline",
    "color",
    "fill",
    "horizontal",
    "vertical",
    "wrap",
    "border",
  ]);
  if (Object.keys(s).some((k) => !allowed.has(k))) return false;
  if (
    s.fontFamily !== undefined &&
    (typeof s.fontFamily !== "string" || s.fontFamily.length > 100)
  )
    return false;
  if (
    s.fontSize !== undefined &&
    !(typeof s.fontSize === "number" && s.fontSize >= 6 && s.fontSize <= 96)
  )
    return false;
  if (
    ["bold", "italic", "underline", "wrap"].some(
      (k) => s[k] !== undefined && typeof s[k] !== "boolean",
    )
  )
    return false;
  if (["color", "fill"].some((k) => s[k] !== undefined && !color(s[k])))
    return false;
  if (
    s.horizontal !== undefined &&
    !["left", "center", "right"].includes(String(s.horizontal))
  )
    return false;
  if (
    s.vertical !== undefined &&
    !["top", "middle", "bottom"].includes(String(s.vertical))
  )
    return false;
  if (s.border !== undefined) {
    const b = s.border;
    if (
      !object(b) ||
      !["thin", "medium", "thick", "dashed", "dotted", "double"].includes(
        String(b.style),
      ) ||
      !color(b.color) ||
      !Array.isArray(b.sides) ||
      b.sides.some((v) => !["top", "right", "bottom", "left"].includes(v))
    )
      return false;
  }
  return true;
}
export function validateCustomExports(value: unknown): string | null {
  const fail = "CUSTOM_DISCIPLINE_EXPORT_INVALID";
  if (
    !object(value) ||
    value.version !== 1 ||
    !Array.isArray(value.profiles) ||
    value.profiles.length > 30
  )
    return fail;
  const keys = new Set<string>();
  for (const p of value.profiles) {
    if (
      !object(p) ||
      typeof p.key !== "string" ||
      !p.key.trim() ||
      keys.has(p.key) ||
      typeof p.name !== "string" ||
      !p.name.trim() ||
      p.name.length > 120 ||
      typeof p.enabled !== "boolean"
    )
      return fail;
    keys.add(p.key);
    if (
      !Array.isArray(p.formats) ||
      !p.formats.length ||
      new Set(p.formats).size !== p.formats.length ||
      p.formats.some((f) => !["CSV", "XLSX", "PDF"].includes(f))
    )
      return fail;
    if (
      typeof p.fileName !== "string" ||
      p.fileName.length > 180 ||
      typeof p.sheetName !== "string" ||
      !p.sheetName ||
      p.sheetName.length > 31 ||
      /[\\/?*\[\]:]/.test(p.sheetName)
    )
      return fail;
    if (
      !Array.isArray(p.columns) ||
      !p.columns.length ||
      p.columns.length > 256
    )
      return fail;
    const cols = new Set<string>(),
      addresses = new Set<string>();
    for (const c of p.columns) {
      if (
        !object(c) ||
        typeof c.key !== "string" ||
        !c.key ||
        cols.has(c.key) ||
        typeof c.label !== "string" ||
        typeof c.fieldKey !== "string" ||
        typeof c.enabled !== "boolean" ||
        !["TEXT", "NUMBER", "BOOLEAN", "DATE", "AUTO"].includes(
          String(c.valueType),
        ) ||
        !isValidCustomDisciplineBehavior(c.behavior) ||
        !excelColumnNumber(String(c.excelColumn)) ||
        !validStyle(c.style) ||
        !validStyle(c.headerStyle) ||
        (c.width !== undefined &&
          !(typeof c.width === "number" && c.width >= 1 && c.width <= 255)) ||
        (c.numberFormat !== undefined && typeof c.numberFormat !== "string")
      )
        return fail;
      cols.add(c.key);
      if (c.enabled) {
        if (addresses.has(String(c.excelColumn))) return fail;
        addresses.add(String(c.excelColumn));
      }
    }
    if (!p.columns.some((c) => c.enabled)) return fail;
    if (!Array.isArray(p.bindings) || p.bindings.length > 200) return fail;
    const bindingCells = new Set<string>();
    for (const b of p.bindings) {
      if (
        !object(b) ||
        typeof b.key !== "string" ||
        typeof b.cell !== "string" ||
        !/^[A-Z]{1,3}[1-9][0-9]{0,6}$/.test(b.cell) ||
        !excelColumnNumber(b.cell.replace(/[0-9]/g, "")) ||
        Number(b.cell.replace(/[A-Z]/g, "")) > 1048576 ||
        typeof b.label !== "string" ||
        typeof b.fieldKey !== "string" ||
        !["TEXT", "NUMBER", "BOOLEAN", "DATE", "AUTO"].includes(
          String(b.valueType),
        ) ||
        !isValidCustomDisciplineBehavior(b.behavior) ||
        !validStyle(b.style) ||
        (b.sheet !== undefined && typeof b.sheet !== "string") ||
        (b.numberFormat !== undefined && typeof b.numberFormat !== "string")
      )
        return fail;
      const address = String(b.sheet ?? p.sheetName) + ":" + b.cell;
      if (bindingCells.has(address)) return fail;
      bindingCells.add(address);
    }
    if (
      ![p.filter, p.order, p.section].every((b) =>
        isValidCustomDisciplineBehavior(b),
      )
    )
      return fail;
    if (
      !Array.isArray(p.sort) ||
      p.sort.some(
        (s) =>
          !object(s) ||
          !cols.has(String(s.columnKey)) ||
          !["asc", "desc"].includes(String(s.direction)),
      )
    )
      return fail;
    if (
      !integer(p.startRow, 1, 1048576) ||
      (p.headerRow !== null && !integer(p.headerRow, 1, 1048576)) ||
      (p.headerRow !== null && Number(p.headerRow) >= Number(p.startRow)) ||
      !integer(p.rowStep, 1, 100) ||
      !integer(p.blankRowsBetweenSections, 0, 100) ||
      (p.endRow !== null && !integer(p.endRow, Number(p.startRow), 1048576)) ||
      (p.copyStyleFromRow !== null &&
        !integer(p.copyStyleFromRow, 1, 1048576)) ||
      (p.templateDataEndRow != null &&
        !integer(p.templateDataEndRow, Number(p.startRow), 1048576)) ||
      typeof p.clearMappedRange !== "boolean" ||
      (p.clearMappedRange && p.endRow === null)
    )
      return fail;
    if (
      ![p.rowHeight, p.headerHeight].every(
        (v) => typeof v === "number" && v >= 10 && v <= 409,
      ) ||
      !validStyle(p.defaultStyle) ||
      !validStyle(p.headerStyle)
    )
      return fail;
    if (
      !object(p.csv) ||
      ![",", ";", "\t"].includes(String(p.csv.delimiter)) ||
      !["bom", "includeHeader", "protectTextFormulas"].every(
        (k) => typeof (p.csv as Record<string, unknown>)[k] === "boolean",
      )
    )
      return fail;
    if (
      !object(p.pdf) ||
      !["A4", "A3"].includes(String(p.pdf.paperSize)) ||
      typeof p.pdf.landscape !== "boolean" ||
      typeof p.pdf.margin !== "number" ||
      p.pdf.margin < 0 ||
      p.pdf.margin > 50 ||
      typeof p.pdf.title !== "string"
    )
      return fail;
    if (p.template !== null) {
      const t = p.template;
      if (
        !object(t) ||
        typeof t.name !== "string" ||
        !t.name.toLowerCase().endsWith(".xlsx") ||
        typeof t.base64 !== "string" ||
        t.base64.length > Math.ceil(EXPORT_TEMPLATE_MAX_BYTES / 3) * 4 ||
        !t.base64.startsWith("UEs") ||
        !/^[A-Za-z0-9+/]*={0,2}$/.test(t.base64) ||
        !Array.isArray(t.sheets) ||
        !t.sheets.includes(p.sheetName)
      )
        return fail;
    }
  }
  return null;
}
export function readCustomExports(
  layouts: unknown,
): CustomExportProfiles | null {
  try {
    const value = typeof layouts === "string" ? JSON.parse(layouts) : layouts;
    const profiles = value?.[CUSTOM_EXPORTS_KEY];
    return profiles && validateCustomExports(profiles) === null
      ? profiles
      : null;
  } catch {
    return null;
  }
}
export function createCustomExportProfile(
  schema: Pick<CustomDisciplineGroupTypeInput, "fields">,
  key: string,
  name: string,
): CustomExportProfile {
  return {
    key,
    name,
    enabled: true,
    formats: ["CSV", "XLSX", "PDF"],
    fileName: "",
    sheetName: "Results",
    columns: schema.fields
      .filter((f) => f.scope === "ATHLETE" && f.isVisible !== false)
      .sort((a, b) => a.order - b.order)
      .map((f, i) => ({
        key: f.key,
        label: f.label,
        fieldKey: f.key,
        enabled: true,
        valueType: f.behavior
          ? "AUTO"
          : f.type === "NUMBER" || f.type === "LIFT"
            ? "NUMBER"
            : f.type === "DATE"
              ? "DATE"
              : "TEXT",
        behavior: f.behavior ? JSON.parse(JSON.stringify(f.behavior)) : null,
        excelColumn: excelColumnName(i + 1),
        width: 18,
      })),
    bindings: [],
    filter: null,
    order: null,
    section: null,
    sort: [],
    startRow: 2,
    headerRow: 1,
    rowStep: 1,
    blankRowsBetweenSections: 1,
    endRow: null,
    rowHeight: 20,
    headerHeight: 28,
    copyStyleFromRow: null,
    clearMappedRange: false,
    defaultStyle: { fontFamily: "Arial", fontSize: 11, vertical: "middle" },
    headerStyle: { bold: true, color: "#ffffff", fill: "#0056b3", wrap: true },
    csv: {
      delimiter: ";",
      bom: true,
      includeHeader: true,
      protectTextFormulas: true,
    },
    pdf: { landscape: true, paperSize: "A4", margin: 8, title: "" },
    template: null,
  };
}
function evaluate(
  behavior: CustomDisciplineBehavior | null,
  fieldKey: string,
  input: CustomDisciplineEvaluateInput,
  key: string,
) {
  try {
    return customWebCellValue({ key, fieldKey, behavior } as CustomWebColumn, {
      ...input,
      instanceKey: key,
    });
  } finally {
    releaseCustomDisciplineBehaviorRuntime(key);
  }
}
function typedValue(
  text: string,
  type: CustomExportColumn["valueType"],
): ExportCellValue {
  if (text === "") return null;
  if (type === "NUMBER") {
    const n = Number(text.replace(",", "."));
    if (!Number.isFinite(n))
      throw Error(`Invalid numeric export value: ${text}`);
    return n;
  }
  if (type === "BOOLEAN") {
    if (text === "true" || text === "1") return true;
    if (text === "false" || text === "0") return false;
    throw Error(`Invalid boolean export value: ${text}`);
  }
  if (type === "DATE") {
    if (!/^\d{4}-\d{2}-\d{2}$/.test(text))
      throw Error(`Invalid export date: ${text}`);
    const date = new Date(text + "T00:00:00Z");
    if (
      !Number.isFinite(date.getTime()) ||
      date.toISOString().slice(0, 10) !== text
    )
      throw Error(`Invalid export date: ${text}`);
    return date;
  }
  if (
    type === "AUTO" &&
    /^-?(?:\d+\.?\d*|\d*\.\d+)$/.test(text) &&
    !/^0\d/.test(text)
  ) {
    const n = Number(text);
    if (Number.isFinite(n)) return n;
  }
  return text;
}
function behaviorStyle(style: Record<string, unknown>): ExportCellStyle {
  const out: ExportCellStyle = {};
  for (const [from, to] of [
    ["color", "color"],
    ["backgroundColor", "fill"],
  ] as const) {
    const v = style[from];
    if (typeof v === "string") {
      if (color(v)) out[to] = v;
      else {
        const rgb = v.match(/^rgb\(\s*(\d+),\s*(\d+),\s*(\d+)\s*\)$/);
        if (rgb && rgb.slice(1).every((n) => Number(n) <= 255))
          out[to] =
            "#" +
            rgb
              .slice(1)
              .map((n) => Number(n).toString(16).padStart(2, "0"))
              .join("");
      }
    }
  }
  if (
    typeof style.fontSize === "number" &&
    style.fontSize >= 6 &&
    style.fontSize <= 96
  )
    out.fontSize = style.fontSize;
  if (typeof style.fontFamily === "string") out.fontFamily = style.fontFamily;
  if (style.fontWeight !== undefined)
    out.bold =
      String(style.fontWeight) === "bold" || Number(style.fontWeight) >= 600;
  if (style.fontStyle !== undefined) out.italic = style.fontStyle === "italic";
  if (["left", "center", "right"].includes(String(style.textAlign)))
    out.horizontal = style.textAlign as ExportCellStyle["horizontal"];
  return out;
}
export function prepareCustomExport(
  profile: CustomExportProfile,
  input: CustomDisciplineEvaluateInput,
): PreparedCustomExport {
  const error = validateCustomExports({ version: 1, profiles: [profile] });
  if (error) throw Error(error);
  const columns = profile.columns.filter((c) => c.enabled),
    groups = input.groups ?? [];
  const context = (
    a?: CustomDisciplineAthleteRecord,
  ): CustomDisciplineEvaluateInput => ({
    ...input,
    athlete: a,
    group: a?.groupId
      ? (groups.find((g) => (g as { id?: unknown }).id === a.groupId) ??
        input.group)
      : input.group,
  });
  const included = input.athletes.filter(
    (a) =>
      !profile.filter ||
      ["true", "1"].includes(
        evaluate(
          profile.filter,
          "",
          context(a),
          `${input.instanceKey}:filter:${a.id}`,
        ).text,
      ),
  );
  const cells = new Map<string, PreparedExportRow["cells"]>();
  for (const a of included) {
    const values: PreparedExportRow["cells"] = {};
    for (const c of columns) {
      const result = evaluate(
        c.behavior,
        c.fieldKey,
        context(a),
        `${input.instanceKey}:cell:${a.id}:${c.key}`,
      );
      values[c.key] = {
        value: typedValue(result.text, c.valueType),
        style: {
          ...profile.defaultStyle,
          ...c.style,
          ...behaviorStyle(result.style),
        },
      };
    }
    cells.set(String(a.id), values);
  }
  const collator = new Intl.Collator(undefined, {
    numeric: true,
    sensitivity: "base",
  });
  let rows = [...included].sort((a, b) => {
    for (const s of profile.sort) {
      const left = cells.get(String(a.id))?.[s.columnKey]?.value,
        right = cells.get(String(b.id))?.[s.columnKey]?.value;
      const difference =
        typeof left === "number" && typeof right === "number"
          ? left - right
          : collator.compare(
              left instanceof Date ? left.toISOString() : String(left ?? ""),
              right instanceof Date ? right.toISOString() : String(right ?? ""),
            );
      if (difference) return s.direction === "asc" ? difference : -difference;
    }
    return 0;
  });
  rows = customWebOrder(
    rows as Array<CustomDisciplineAthleteRecord & { id: string }>,
    profile.order,
    { ...input, athletes: rows, instanceKey: input.instanceKey + ":order" },
  );
  let rowNumber = profile.startRow,
    previousSection: string | undefined;
  const prepared = rows.map((a, index) => {
    const section = profile.section
      ? evaluate(
          profile.section,
          "",
          context(a),
          `${input.instanceKey}:section:${a.id}`,
        ).text
      : "";
    if (index > 0) {
      rowNumber += profile.rowStep;
      if (section !== previousSection)
        rowNumber += profile.blankRowsBetweenSections;
    }
    previousSection = section;
    if (
      rowNumber > 1048576 ||
      (profile.endRow !== null && rowNumber > profile.endRow)
    )
      throw Error(
        `CUSTOM_EXPORT_CAPACITY:${rowNumber}:${profile.endRow ?? 1048576}`,
      );
    return {
      athleteId: String(a.id),
      groupId: String(a.groupId ?? ""),
      section,
      rowNumber,
      cells: cells.get(String(a.id))!,
    };
  });
  const extraTemplateRows = customExportTemplateRowExpansion(profile, prepared);
  const bindings = profile.bindings.map((b) => ({
    ...b,
    cell:
      extraTemplateRows &&
      (b.sheet ?? profile.sheetName) === profile.sheetName &&
      Number(b.cell.replace(/[A-Z]/g, "")) > profile.templateDataEndRow!
        ? b.cell.replace(/\d+$/, (row) =>
            String(Number(row) + extraTemplateRows),
          )
        : b.cell,
    value: typedValue(
      evaluate(
        b.behavior,
        b.fieldKey,
        { ...input, athlete: undefined },
        `${input.instanceKey}:binding:${b.key}`,
      ).text,
      b.valueType,
    ),
  }));
  for (const binding of bindings) {
    if ((binding.sheet ?? profile.sheetName) !== profile.sheetName) continue;
    const col = binding.cell.replace(/[0-9]/g, ""),
      r = Number(binding.cell.replace(/[A-Z]/g, ""));
    if (
      columns.some((c) => c.excelColumn === col) &&
      (r === profile.headerRow || prepared.some((row) => row.rowNumber === r))
    )
      throw Error(`CUSTOM_EXPORT_CELL_CONFLICT:${binding.cell}`);
  }
  return { columns, rows: prepared, bindings };
}
export function exportCellText(value: ExportCellValue): string {
  return value instanceof Date
    ? value.toISOString().slice(0, 10)
    : value === null
      ? ""
      : String(value);
}
export function customExportCsv(
  profile: CustomExportProfile,
  data: PreparedCustomExport,
): string {
  const quote = (value: ExportCellValue) => {
    let text = exportCellText(value);
    if (
      profile.csv.protectTextFormulas &&
      typeof value === "string" &&
      /^[\s]*[=+\-@]/.test(text)
    )
      text = "'" + text;
    return /[\r\n"\t,;]/.test(text)
      ? '"' + text.replace(/"/g, '""') + '"'
      : text;
  };
  const lines: string[] = [];
  if (profile.csv.includeHeader)
    lines.push(
      data.columns.map((c) => quote(c.label)).join(profile.csv.delimiter),
    );
  let previous: string | undefined;
  for (const row of data.rows) {
    if (previous !== undefined && previous !== row.section)
      for (let i = 0; i < profile.blankRowsBetweenSections; i++) lines.push("");
    lines.push(
      data.columns
        .map((c) => quote(row.cells[c.key].value))
        .join(profile.csv.delimiter),
    );
    previous = row.section;
  }
  return (profile.csv.bom ? "\ufeff" : "") + lines.join("\r\n") + "\r\n";
}
/** Ordinary editable graph preset; the caller selects both the status field and its failure value. */
export function signedAttemptExportBehavior(
  valueKey: string,
  statusKey: string,
  failureValue: string,
): CustomDisciplineBehavior {
  const nodes: any[] = [],
    connections: any[] = [];
  let serial = 0;
  const node = (spec: any, ports: any[]) => {
    const id = "export-" + ++serial;
    nodes.push({
      id,
      x: (serial % 4) * 260,
      y: Math.floor(serial / 4) * 140,
      ...spec,
      ports,
    });
    return id;
  };
  const port = (
    id: string,
    direction: string,
    valueType: string,
    value?: unknown,
  ) => ({
    id,
    label: id,
    direction,
    valueType,
    ...(value !== undefined ? { value } : {}),
  });
  const wire = (from: string, to: string, input: string) =>
    connections.push({
      id: "wire-" + connections.length,
      fromNodeId: from,
      fromPortId: "output",
      toNodeId: to,
      toPortId: input,
    });
  const literal = (value: any, type = "string") =>
    node({ type: "variable", valueType: type }, [
      port("output", "output", type, value),
    ]);
  const op = (operator: string, inputs: string[], type: string) => {
    const id = node(
      {
        type: "operator",
        operator,
        mode:
          inputs.length === 1
            ? "unary"
            : inputs.length === 2
              ? "binary"
              : "ternary",
      },
      [
        ...inputs.map((_, i) =>
          port("input" + (i + 1), "input", i === 2 ? "boolean" : "string"),
        ),
        port("output", "output", type),
      ],
    );
    inputs.forEach((from, i) => wire(from, id, "input" + (i + 1)));
    return id;
  };
  const athlete = node(
      { type: "variable", valueType: "object", objectSource: "athlete" },
      [port("output", "output", "object")],
    ),
    value = op("getByPath", [athlete, literal(valueKey)], "string"),
    status = op("getByPath", [athlete, literal(statusKey)], "string"),
    numeric = op("castToNumber", [value], "number"),
    abs = op(
      "ifElse",
      [
        op("subtract", [literal(0, "number"), numeric], "number"),
        numeric,
        op("lessThan", [numeric, literal(0, "number")], "boolean"),
      ],
      "number",
    ),
    failed = op("equals", [status, literal(failureValue)], "boolean"),
    signed = op(
      "ifElse",
      [op("subtract", [literal(0, "number"), abs], "number"), abs, failed],
      "number",
    ),
    output = op(
      "ifElse",
      [literal(""), signed, op("empty", [value], "boolean")],
      "string",
    );
  const event = node(
      {
        type: "event",
        kind: "onChange",
        objectSource: "athlete",
        triggerOnInit: true,
      },
      [port("output", "output", "flow")],
    ),
    set = node({ type: "action", kind: "set", key: "setBinding" }, [
      port("in", "input", "flow"),
      port("value", "input", "string"),
      port("out", "output", "flow"),
    ]);
  wire(event, set, "in");
  wire(output, set, "value");
  return { version: 1, graph: { nodes, connections } };
}

export type ExportTemplateInspection = {
  sheets: Array<{
    name: string;
    rowCount: number;
    columnCount: number;
    rows: Array<{
      row: number;
      cells: Array<{
        column: string;
        value: string;
        style: ExportCellStyle;
        skip?: boolean;
        colSpan?: number;
        rowSpan?: number;
        width?: number;
      }>;
    }>;
  }>;
};
export type CustomExportAdminServices = {
  inspect: (file: File) => Promise<ExportTemplateInspection>;
  download: (
    profile: CustomExportProfile,
    input: CustomDisciplineEvaluateInput,
  ) => Promise<Blob>;
};

/** Export workbooks stay out of public/display catalogs. */
export function publicCustomDisciplineLayouts(
  value: unknown,
): Record<string, Record<string, unknown>> {
  const layouts = typeof value === "string" ? JSON.parse(value) : value;
  return object(layouts)
    ? (Object.fromEntries(
        Object.entries(layouts).filter(([key]) => key !== CUSTOM_EXPORTS_KEY),
      ) as Record<string, Record<string, unknown>>)
    : {};
}
/** A public/offline catalog refresh must not discard an authenticated template cache. */
export function mergeCustomExportLayoutCache(
  remote: unknown,
  local: unknown,
  exportProfilesIncluded: boolean,
): unknown {
  if (exportProfilesIncluded) return remote;
  const previous = typeof local === "string" ? JSON.parse(local) : local;
  if (!object(previous) || !previous[CUSTOM_EXPORTS_KEY]) return remote;
  return {
    ...publicCustomDisciplineLayouts(remote),
    [CUSTOM_EXPORTS_KEY]: previous[CUSTOM_EXPORTS_KEY],
  };
}
